// src/components/home/PlanTripSection.jsx
// Home page "Plan a Trip" tab: pick a curated trip, optionally add start city, date, adults and
// days, then open /trips/:slug. Whatever is filled in travels in the URL:
//   city + date  -> the trip page starts live routes and hotels straight away
//   only some    -> the trip page opens its start popup, prefilled with what was given
//   nothing      -> the plain itinerary (the popup shows later, as usual)
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, MapPin, Calendar, Users, Sun, Minus, Plus, ArrowRight, Sparkles } from "lucide-react";
import { CURATED_TRIPS } from "../../curated/curatedTripsData";
import OriginCityInput from "../common/OriginCityInput";
import { placeToQuery } from "../../curated/originPlace";
import { getTripCover } from "../../curated/tripCovers";
import { SHOWCASE_ONLY, showcaseTrips, showcaseLink } from "../../curated/showcase";

// Showcase mode: the DMC trip cards, shaped like curated trips for this picker
const SHOWCASE = SHOWCASE_ONLY ? showcaseTrips().map((c) => ({
    slug: c.key, title: c.title, subtitle: c.subtitle, vibe: c.vibe, best_months: c.best_months, max_group: c.max_group,
    days: Array.from({ length: c.days }), cover: c.cover, cover_gradient: c.gradient, _card: c,
})) : null;

const pad = (n) => String(n).padStart(2, "0");
const isoDay = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const tomorrowISO = () => { const d = new Date(); d.setDate(d.getDate() + 1); return isoDay(d); };
const shortDate = (iso) => {
    try { const [y, m, d] = iso.split("-").map(Number); return new Date(y, m - 1, d).toLocaleDateString("en-IN", { day: "numeric", month: "short" }); }
    catch { return iso; }
};
const tripName = (t) => String(t.title || "").split(":")[0].trim();

export default function PlanTripSection() {
    const navigate = useNavigate();
    const trips = SHOWCASE || CURATED_TRIPS || [];
    const [slug, setSlug] = useState(trips[0]?.slug || "");
    const trip = useMemo(() => trips.find((t) => t.slug === slug) || trips[0], [trips, slug]);
    const baseDays = trip?.days?.length || 3;

    // Start place: any station or airport from the lists ({ name, lat, lng, code }), or none yet
    const [origin, setOrigin] = useState(null);
    const city = origin?.name || "";
    const [date, setDate] = useState("");
    const [adults, setAdults] = useState(2);
    const [days, setDays] = useState(baseDays);
    const maxAdults = trip?.max_group || 8;
    const maxDays = Math.max(10, baseDays);

    // A new trip brings its own length; adults stay, capped at the trip's group size
    useEffect(() => { setDays(baseDays); setAdults((a) => Math.min(a, maxAdults)); }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

    // Keep the picked card in view (keyboard or after a programmatic change)
    const railRef = useRef(null);
    useEffect(() => {
        const el = railRef.current?.querySelector(`[data-slug="${slug}"]`);
        el?.scrollIntoView?.({ block: "nearest", inline: "nearest", behavior: "smooth" });
    }, [slug]);

    if (!trip) return null;

    const live = !!(city && date);
    const go = () => {
        const q = new URLSearchParams();
        if (origin) placeToQuery(q, origin);
        if (date) q.set("date", date);
        // Adults always ride along once there's a city or date, so the trip page gets the full set
        if (adults !== 2 || city || date) q.set("adults", String(adults));
        if (days !== baseDays) q.set("days", String(days));
        const qs = q.toString();
        if (trip._card) { navigate(showcaseLink(trip._card, { city: origin, date, adults, days })); return; }
        navigate(`/trips/${trip.slug}${qs ? `?${qs}` : ""}`);
    };

    const summary = [
        tripName(trip),
        `${days} day${days > 1 ? "s" : ""}`,
        `${adults} adult${adults > 1 ? "s" : ""}`,
        city ? `from ${city}` : null,
        date ? shortDate(date) : null,
    ].filter(Boolean).join(" · ");

    return (
        <section className="rc-pt" aria-label="Plan a trip">
            <style>{CSS}</style>

            <div className="rc-pt-step"><span className="rc-pt-num">1</span> Pick a trip</div>
            <div className="rc-pt-rail" ref={railRef} role="radiogroup" aria-label="Trips">
                {trips.map((t) => {
                    const on = t.slug === trip.slug;
                    const cover = t.cover || getTripCover?.(t);
                    return (
                        <button
                            key={t.slug}
                            type="button"
                            role="radio"
                            aria-checked={on}
                            data-slug={t.slug}
                            className={`rc-pt-card${on ? " on" : ""}`}
                            onClick={() => setSlug(t.slug)}
                            style={{
                                backgroundImage: cover
                                    ? `linear-gradient(180deg, rgba(15,23,42,0) 25%, rgba(15,23,42,.55) 60%, rgba(15,23,42,.92) 100%), url(${cover})`
                                    : `linear-gradient(180deg, rgba(15,23,42,0) 25%, rgba(15,23,42,.45) 60%, rgba(15,23,42,.8) 100%), ${t.cover_gradient || "linear-gradient(135deg,#FF7A3D,#F59E0B)"}`,
                            }}
                        >
                            {on && <span className="rc-pt-check" aria-hidden="true"><Check size={14} strokeWidth={3.2} /></span>}
                            <span className="rc-pt-card-name">{tripName(t)}</span>
                            <span className="rc-pt-card-sub">{t._card?.festival ? t._card.when : `${t.days.length} days`}{t.best_months ? ` · ${t.best_months}` : ""}</span>
                        </button>
                    );
                })}
            </div>

            {(trip.vibe?.length > 0 || trip.subtitle) && (
                <div className="rc-pt-vibe">
                    <Sparkles size={14} className="rc-pt-vibe-ic" aria-hidden="true" />
                    {trip.vibe?.length > 0
                        ? trip.vibe.slice(0, 4).map((v) => <span key={v} className="rc-pt-tag">{v}</span>)
                        : <span className="rc-pt-vibe-text">{trip.subtitle}</span>}
                </div>
            )}

            <div className="rc-pt-step">
                <span className="rc-pt-num">2</span> Make it yours <span className="rc-pt-opt">optional</span>
            </div>
            <div className="rc-pt-fields">
                <div className="rc-pt-field">
                    <span className="rc-pt-lbl"><MapPin size={13} /> Starting from</span>
                    <OriginCityInput value={city} onPick={setOrigin} placeholder="Station or airport" ariaLabel="Starting from"
                        className="rc-pt-input rc-pt-text" minWidth={300} />
                </div>
                <label className="rc-pt-field">
                    <span className="rc-pt-lbl"><Calendar size={13} /> Start date</span>
                    <input
                        type="date" value={date} min={tomorrowISO()}
                        onChange={(e) => setDate(e.target.value)}
                        onClick={(e) => { try { e.currentTarget.showPicker?.(); } catch { /* not allowed */ } }}
                        className={`rc-pt-input${date ? "" : " ph"}`}
                    />
                </label>
                <div className="rc-pt-field">
                    <span className="rc-pt-lbl"><Users size={13} /> Adults</span>
                    <Stepper value={adults} min={1} max={maxAdults} onChange={setAdults} label="adults" />
                </div>
                <div className="rc-pt-field">
                    <span className="rc-pt-lbl"><Sun size={13} /> Days</span>
                    <Stepper value={days} min={2} max={maxDays} onChange={setDays} label="days" />
                </div>
            </div>

            <div className="rc-pt-foot">
                <div className="rc-pt-sum">
                    <div className="rc-pt-sum-main">{summary}</div>
                    <div className={`rc-pt-sum-note${live ? " live" : ""}`}>
                        {live ? "Live trains, flights and hotels for your dates" : "Add your city and date now or on the trip page for live prices"}
                    </div>
                </div>
                <button type="button" className="rc-pt-cta" onClick={go}>
                    View your itinerary <ArrowRight size={18} strokeWidth={2.6} />
                </button>
            </div>
        </section>
    );
}

function Stepper({ value, min, max, onChange, label }) {
    return (
        <div className="rc-pt-stepper">
            <button type="button" onClick={() => value > min && onChange(value - 1)} disabled={value <= min} aria-label={`Fewer ${label}`}><Minus size={15} strokeWidth={2.6} /></button>
            <b aria-live="polite">{value}</b>
            <button type="button" onClick={() => value < max && onChange(value + 1)} disabled={value >= max} aria-label={`More ${label}`}><Plus size={15} strokeWidth={2.6} /></button>
        </div>
    );
}

const CSS = `
.rc-pt{max-width:980px;width:calc(100% - 32px);margin:0 auto;position:relative;z-index:5;box-sizing:border-box;
  background:#fff;border:1px solid #E7E2DA;border-radius:20px;padding:20px 22px 18px;text-align:left;
  box-shadow:0 18px 40px -28px rgba(15,23,42,.35);font-family:'Inter',-apple-system,system-ui,sans-serif}
.rc-pt *{box-sizing:border-box}
.rc-pt-step{display:flex;align-items:center;gap:8px;font-size:13px;font-weight:700;color:#0F172A;margin:0 0 10px}
.rc-pt-step + .rc-pt-fields{margin-top:0}
.rc-pt-num{width:20px;height:20px;border-radius:50%;background:#FF4E01;color:#fff;font-size:11px;font-weight:800;
  display:inline-flex;align-items:center;justify-content:center}
.rc-pt-opt{font-size:11px;font-weight:600;color:#94A3B8;text-transform:uppercase;letter-spacing:.06em}

/* The rail scrolls, so it clips anything outside it. 6px of padding (offset by a -6px margin, so the
   cards still line up with the panel) leaves room for the 3px selected ring and the hover lift. */
.rc-pt-rail{display:flex;gap:12px;overflow-x:auto;padding:6px 6px 12px;margin:-2px -6px 0;scroll-padding-inline:6px;scroll-snap-type:x proximity;
  scrollbar-width:thin}
.rc-pt-rail::-webkit-scrollbar{height:6px}.rc-pt-rail::-webkit-scrollbar-thumb{background:#E2E8F0;border-radius:99px}
/* Exactly 4 cards across the panel (3 gaps of 12px); any more scroll sideways */
.rc-pt-card{position:relative;flex:0 0 calc((100% - 36px) / 4);height:132px;border:0;border-radius:14px;padding:10px 12px;cursor:pointer;
  background-size:cover;background-position:center;color:#fff;text-align:left;scroll-snap-align:start;
  display:flex;flex-direction:column;justify-content:flex-end;gap:2px;
  box-shadow:0 0 0 1px rgba(15,23,42,.06);transition:transform .18s ease, box-shadow .18s ease}
.rc-pt-card:hover{transform:translateY(-2px);box-shadow:0 10px 20px -12px rgba(15,23,42,.5)}
.rc-pt-card.on{box-shadow:0 0 0 3px #FF4E01, 0 10px 22px -12px rgba(255,78,1,.6)}
.rc-pt-card:focus-visible{outline:none;box-shadow:0 0 0 3px #0F172A}
/* Name stays readable on light photos: darker bottom fade plus a soft shadow under the text */
.rc-pt-card-name{font-size:19px;font-weight:800;line-height:1.15;letter-spacing:-.01em;color:#fff;
  text-shadow:0 1px 2px rgba(0,0,0,.55), 0 2px 12px rgba(0,0,0,.45)}
.rc-pt-card-sub{font-size:12px;font-weight:600;color:rgba(255,255,255,.95);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;
  text-shadow:0 1px 2px rgba(0,0,0,.6)}
.rc-pt-check{position:absolute;top:8px;right:8px;width:24px;height:24px;border-radius:50%;background:#FF4E01;color:#fff;
  display:flex;align-items:center;justify-content:center;box-shadow:0 2px 6px rgba(0,0,0,.25)}

.rc-pt-vibe{display:flex;align-items:center;gap:6px;flex-wrap:wrap;margin:2px 0 16px;font-size:12.5px;color:#57534E}
.rc-pt-vibe-ic{color:#FF4E01}
.rc-pt-tag{background:#FFF4EE;color:#C2410C;font-weight:600;font-size:12px;padding:3px 10px;border-radius:99px}

.rc-pt-fields{display:grid;grid-template-columns:1.3fr 1.2fr 1fr 1fr;gap:10px;margin-bottom:16px}
.rc-pt-field{display:flex;flex-direction:column;gap:5px;min-width:0}
.rc-pt-lbl{display:flex;align-items:center;gap:5px;font-size:11px;font-weight:700;color:#64748B;text-transform:uppercase;letter-spacing:.05em}
.rc-pt-input{height:44px;width:100%;border:1.5px solid #E2E8F0;border-radius:12px;padding:0 12px;background:#fff;
  font-family:inherit;font-size:15px;font-weight:600;color:#0F172A;cursor:pointer;transition:border-color .15s}
.rc-pt-input.ph{color:#94A3B8;font-weight:500}
.rc-pt-text{cursor:text}
.rc-pt-text::placeholder{color:#94A3B8;font-weight:500}
.rc-pt-input:hover{border-color:#CBD5E1}
.rc-pt-input:focus{outline:none;border-color:#FF4E01;box-shadow:0 0 0 3px rgba(255,78,1,.12)}
.rc-pt-stepper{height:44px;display:flex;align-items:center;justify-content:space-between;border:1.5px solid #E2E8F0;border-radius:12px;padding:0 5px}
.rc-pt-stepper b{font-size:16px;color:#0F172A;min-width:22px;text-align:center}
.rc-pt-stepper button{width:32px;height:32px;border-radius:9px;border:0;background:#F1F5F9;color:#0F172A;cursor:pointer;
  display:flex;align-items:center;justify-content:center;padding:0;transition:background .15s}
.rc-pt-stepper button:hover:not(:disabled){background:#FFE7DB;color:#C2410C}
.rc-pt-stepper button:disabled{opacity:.35;cursor:default}

.rc-pt-foot{display:flex;align-items:center;justify-content:space-between;gap:16px;padding-top:14px;border-top:1px solid #F1F5F9}
.rc-pt-sum{min-width:0}
.rc-pt-sum-main{font-size:14.5px;font-weight:700;color:#0F172A;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.rc-pt-sum-note{font-size:12.5px;color:#64748B;margin-top:2px}
.rc-pt-sum-note.live{color:#047857;font-weight:600}
.rc-pt-cta{flex-shrink:0;display:inline-flex;align-items:center;gap:8px;height:50px;padding:0 24px;border:0;border-radius:14px;
  background:#FF4E01;color:#fff;font-family:inherit;font-size:16px;font-weight:800;cursor:pointer;
  box-shadow:0 10px 22px -10px rgba(255,78,1,.8);transition:background .15s, transform .15s}
.rc-pt-cta:hover{background:#E64600;transform:translateY(-1px)}
.rc-pt-cta:focus-visible{outline:2px solid #0F172A;outline-offset:2px}
.rc-pt-cta svg{transition:transform .15s}.rc-pt-cta:hover svg{transform:translateX(3px)}

@media (max-width: 768px){
  .rc-pt{padding:16px 14px 14px;border-radius:18px}
  .rc-pt-card{flex-basis:150px;height:108px}
  .rc-pt-card-name{font-size:17px}
  .rc-pt-fields{grid-template-columns:1fr 1fr}
  .rc-pt-foot{flex-direction:column;align-items:stretch;gap:12px}
  .rc-pt-sum-main{white-space:normal}
  .rc-pt-cta{justify-content:center;width:100%}
}
@media (prefers-reduced-motion: reduce){.rc-pt-card,.rc-pt-cta,.rc-pt-cta svg{transition:none}}
`;
